'use client'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getLodgeBySlug, getAdventureById } from '@/lib/data'
import { useActivityVisibility } from '@/hooks/useActivityVisibility'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import styles from '@/styles/lodge-page.module.css'
import { BreadcrumbSchema, LodgeSchema } from '@/lib/schema'
import BookNowCTA from '@/components/BookNowCTA'


export default function ApexParvaCavePage() {
  const swiperRef = useRef<SwiperType | null>(null)
  const lodge = getLodgeBySlug('apex-parva-cave')
  const { isVisible, loaded } = useActivityVisibility()
  const router = useRouter()
  const t = useTranslations('lodges')
  const tPage = useTranslations('lodgesPage')
  const tCommon = useTranslations('common')
  const locale = useLocale()

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy(true, true)
        swiperRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (loaded && lodge && !isVisible(lodge.id, lodge.comingSoon)) {
      router.replace(`/${locale}/lodges`)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  if (!lodge) {
    return <div>{tPage('ui.lodgeNotFound')}</div>
  }

  if (loaded && !isVisible(lodge.id, lodge.comingSoon)) {
    return null
  }

  // Get translated lodge data
  const lodgeData = {
    name: t('apex-parva-cave.name'),
    subtitle: t('apex-parva-cave.subtitle'),
    location: t('apex-parva-cave.location'),
    sectionTitle: t('apex-parva-cave.sectionTitle'),
    sectionDescription: t('apex-parva-cave.sectionDescription'),
    detailsTitle: t('apex-parva-cave.detailsTitle'),
    detailsDescription: t('apex-parva-cave.detailsDescription'),
    schemaDescription: t('apex-parva-cave.schemaDescription'),
  }

  // Get translated arrays and objects
  const translatedStats = t.raw('apex-parva-cave.stats') as Record<string, { number: string; label: string; description: string }>
  const translatedActivities = t.raw('apex-parva-cave.activities') as Record<string, { title: string; description: string }>
  const translatedGuestRooms = t.raw('apex-parva-cave.guestRooms') as string[]
  const translatedFacilities = t.raw('apex-parva-cave.facilities') as string[]
  const translatedSpecialFeatures = t.raw('apex-parva-cave.specialFeatures') as string[]

  // Convert stats object to array for mapping
  const statsArray = Object.values(translatedStats)

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: tCommon('lodges'), href: `/${locale}/lodges/` },
        { name: lodgeData.name, href: `/${locale}/lodges/${lodge.slug}/` },
      ]} />
      <LodgeSchema
        name={lodgeData.name}
        description={lodgeData.schemaDescription}
        image={lodge.heroImage}
        url={`/${locale}/lodges/${lodge.slug}/`}
        address={{ locality: 'La Parva', region: 'Region Metropolitana', country: 'CL' }}
        geo={{ latitude: -33.3400, longitude: -70.2800 }}
        amenities={['Ski-in/Ski-out Access', 'Outdoor Hot Tub', 'Fireplace', 'Ski Storage', 'Backcountry Equipment', ...lodge.facilities]}
        numberOfRooms={4}
      />
      <Header />

      <HeroParallax
        backgroundImage={lodge.heroImage}
        title={lodgeData.name}
        subtitle={lodgeData.subtitle}
        location={lodgeData.location}
      />

      <main>
        {/* Gallery Ticker with Stats & Numbers */}
        <section className={styles.creamSpacer}>
          <div className="container">
            {/* Section Header */}
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{lodgeData.sectionTitle}</h2>
              <p className={styles.sectionDescription}>
                {lodgeData.sectionDescription}
              </p>
            </div>
          </div>

          {/* Infinite Ticker */}
          <div className={styles.tickerContainer}>
            <div className={styles.tickerTrack} style={{ '--ticker-count': lodge.gallery.length } as React.CSSProperties}>
              {lodge.gallery.map((image, index) => (
                <div key={`ticker-1-${index}`} className={styles.tickerItem}>
                  <img src={image} alt={lodge.galleryAlts[index]} loading="lazy" />
                </div>
              ))}
              {lodge.gallery.map((image, index) => (
                <div key={`ticker-2-${index}`} className={styles.tickerItem}>
                  <img src={image} alt={lodge.galleryAlts[index]} loading="lazy" />
                </div>
              ))}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="container">
            <div className={styles.statsGrid}>
              {statsArray.map((stat, index) => (
                <div key={index} className={styles.statCard}>
                  <div className={styles.statNumber}>{stat.number}</div>
                  <div className={styles.statLabel}>{stat.label}</div>
                  <div className={styles.statDescription}>{stat.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Narrative Description */}
          <div className="container">
            <div className={styles.narrativeSection}>
              <p className={styles.narrativeText}>{lodge.narrativeText}</p>
            </div>
          </div>

          {/* Signature Features */}
          <div className="container">
            <div className={styles.featuresList}>
              {lodge.features.map((feature, index) => (
                <div key={index} className={styles.featureItem}>
                  <div className={styles.featureIcon}>&#9670;</div>
                  <div className={styles.featureContent}>
                    <h3 className={styles.featureTitle}>{feature.title}</h3>
                    <p className={styles.featureText}>{feature.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </section>

        <BookNowCTA
          variant="inline"
          productId="apex-parva-cave"
          productType="lodge"
          productName={lodgeData.name}
          bookingMode={lodge.bookingMode || 'hidden'}
          bookingUrl="/booking/lodges/apex-parva-cave"
        />

        {/* Image Gallery Carousel - Swiper with Navigation */}
        <section className={styles.lodgeGallery}>
          <div className={styles.lodgeCarousel}>
            <Swiper
              modules={[Navigation]}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              slidesPerView={1}
              spaceBetween={64}
              centeredSlides={true}
              loop={lodge.carouselImages.length > 3}
              speed={800}
              grabCursor={true}
              navigation={{
                nextEl: `.${styles.swiperButtonNext}`,
                prevEl: `.${styles.swiperButtonPrev}`,
              }}
              breakpoints={{
                768: {
                  spaceBetween: 80,
                },
                1024: {
                  spaceBetween: 96,
                },
                1440: {
                  spaceBetween: 112,
                },
              }}
              className={styles.lodgeSwiper}
            >
              {lodge.carouselImages.map((image, index) => (
                <SwiperSlide key={index} className={styles.carouselCell}>
                  <img src={image} alt={lodge.carouselAlts[index]} loading="lazy" />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Navigation arrows */}
            <div className={`${styles.swiperButtonPrev} swiper-button-prev`}></div>
            <div className={`${styles.swiperButtonNext} swiper-button-next`}></div>
          </div>
        </section>


        <BookNowCTA
          variant="section"
          productId="apex-parva-cave"
          productType="lodge"
          productName={lodgeData.name}
          bookingMode={lodge.bookingMode || 'hidden'}
          bookingUrl="/booking/lodges/apex-parva-cave"
        />

        {/* Lodge Details Section */}
        <section className={styles.lodgeDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              <div className={styles.detailsHeader}>
                <div className={styles.detailsLabel}>{tPage('ui.lodgeDetails')}</div>
                <h2 className={styles.detailsTitle}>{lodgeData.detailsTitle}</h2>
                <p className={styles.detailsDescription}>
                  {lodgeData.detailsDescription}
                </p>
              </div>
              <div className={styles.detailsList}>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{tPage('ui.guestRooms')}</div>
                  <div className={styles.detailContent}>
                    <ul>
                      {translatedGuestRooms.map((room, index) => (
                        <li key={index}>{room}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{tPage('ui.facilities')}</div>
                  <div className={styles.detailContent}>
                    <ul>
                      {translatedFacilities.map((facility, index) => (
                        <li key={index}>{facility}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{tPage('ui.specialFeatures')}</div>
                  <div className={styles.detailContent}>
                    <ul>
                      {translatedSpecialFeatures.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>


        <BookNowCTA
          variant="closing"
          productId="apex-parva-cave"
          productType="lodge"
          productName={lodgeData.name}
          bookingMode={lodge.bookingMode || 'hidden'}
          bookingUrl="/booking/lodges/apex-parva-cave"
        />

        {/* Available Activities */}
        <section className={styles.creamSection}>
          <div className="container">
            <h2>{tPage('ui.availableActivities')}</h2>
            <div className={styles.lodgesGrid}>
              {lodge.activities.map((activity, index) => {
                const adventure = getAdventureById(activity.adventureId)
                if (!adventure || !isVisible(adventure.id, adventure.hidden)) return null
                const translatedActivity = translatedActivities[activity.adventureId]

                return (
                  <article key={index} className={styles.lodgeCard}>
                    <div className={styles.lodgeImage}>
                      <img
                        src={adventure.heroImage}
                        alt={translatedActivity?.title || activity.title}
                        loading="lazy"
                      />
                    </div>
                    <div className={styles.lodgeOverlay}>
                      <h3 className={styles.lodgeTitle}>{translatedActivity?.title || activity.title}</h3>
                      <div className={styles.lodgeContent}>
                        <p>{translatedActivity?.description || activity.description}</p>
                        <a
                          href={`/${locale}/guided-adventures/${activity.adventureId}`}
                          className={styles.lodgeLink}
                        >
                          {tPage('ui.explore')} &rarr;
                        </a>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </>
  )
}
